/**
 * OPUS-913 — User Guide: floating help button + guided tour (ported from the
 * Claude Design handoff userguide.jsx). Optional + skippable; auto-opens once on
 * first visit (localStorage flag), and is re-openable any time from the button.
 * Each step navigates to its screen via react-router.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, IconButton, Button } from "../common/opus";

const SEEN_KEY = "opus-guide-seen";

const GUIDE_STEPS = [
  { route: "/dashboard/renderer-management", icon: "rendererList", title: "Renderer list", body: "Your live render outputs. Each card shows the output ID, bound scene, environment and who created it. Click a renderer to open Scene Controls, or use the </> and monitor icons to jump to controls or open the output." },
  { route: "/dashboard/renderer-management", icon: "plus", title: "Add a renderer", body: "Use “Add Renderer” to provision a new output — set type, status, optionally create a preview renderer, then bind a scene and pick aspect ratio, resolution and frame rate. Toggle “Edit” on the list to reveal delete actions." },
  { route: "/dashboard/scene-management", icon: "scene", title: "Scene list", body: "Browse and manage scene graphics. The folder icon opens scene assets, the pencil edits the scene and its captions, and the lock manages permissions. Search by name or creator." },
  { route: "/dashboard/user-management", icon: "users", title: "Users", body: "Manage who can access this organisation. Add users with a role and organisation; the activity icon opens their detail, and Edit mode reveals removal. Super admins can also manage entitlements, resource grants and access locks." },
  { route: "/dashboard/roles", icon: "shield", title: "Roles & permissions", body: "Define what each role can do. Open a role to tick individual permissions by domain, or grant the all-permissions wildcard (*). The status toggle activates or deactivates a role. Backend enforces; the UI follows." },
  { route: "/dashboard/organisation", icon: "org", title: "Organisation", body: "Every organisation has its own users, renderers, scenes and entitlements. The eye icon opens an overview; the lock manages a region/IP access lock. Super admins can add organisations." },
  { route: "/dashboard/settings", icon: "settings", title: "Settings · Quick Links", body: "Manage the sidebar CTA launchers. Add or edit a CTA with its name, sequence, logo, href, whether it’s a local endpoint, and which URL parameters to append. Toggle “Show only to me” for a private link." },
  { route: "/dashboard/activity-logs", icon: "activity", title: "Activity logs", body: "A full audit trail of every create, update, delete and login across your organisation, grouped by day with the actor, target, time and IP." },
];

function GuideTour({ open, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  useEffect(() => { if (open) setStep(0); }, [open]);
  useEffect(() => {
    if (open && GUIDE_STEPS[step]) { try { navigate(GUIDE_STEPS[step].route); } catch (_) { /* route guard handles access */ } }
  }, [step, open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const s = GUIDE_STEPS[step];
  const last = step === GUIDE_STEPS.length - 1;
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1290, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(1.5px)", animation: "opusFade .15s ease" }} />
      <div role="dialog" aria-label="User guide" style={{
        position: "fixed", right: 22, bottom: 22, zIndex: 1300, width: 380, maxWidth: "calc(100vw - 44px)",
        background: "var(--elevated)", border: "1px solid var(--border-strong)", borderRadius: "var(--r-lg)",
        boxShadow: "var(--shadow-lg)", overflow: "hidden", animation: "opusPop .2s cubic-bezier(.2,.8,.3,1)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "16px 18px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ width: 34, height: 34, borderRadius: "var(--r-md)", background: "rgba(255,69,92,0.13)", color: "var(--coral)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name={s.icon} size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Guided tour · {step + 1} of {GUIDE_STEPS.length}</div>
            <div style={{ fontSize: 15.5, fontWeight: 700, color: "var(--text)", marginTop: 1 }}>{s.title}</div>
          </div>
          <IconButton icon="x" title="Close guide" onClick={onClose} />
        </div>
        <div style={{ padding: "16px 18px" }}>
          <p style={{ fontSize: 13.5, color: "var(--text-muted)", lineHeight: 1.55, margin: 0 }}>{s.body}</p>
        </div>
        <div style={{ display: "flex", gap: 5, padding: "0 18px 14px" }}>
          {GUIDE_STEPS.map((_, i) => (
            <button key={i} type="button" onClick={() => setStep(i)} aria-label={`Step ${i + 1}`}
              style={{ flex: 1, height: 4, borderRadius: 99, border: "none", cursor: "pointer", padding: 0, background: i <= step ? "var(--coral)" : "var(--surface-3)", transition: "background .2s" }} />
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "14px 18px", borderTop: "1px solid var(--border)", background: "var(--surface-0)" }}>
          <Button variant="ghost" size="sm" onClick={onClose}>Skip tour</Button>
          <div style={{ display: "flex", gap: 8 }}>
            {step > 0 && <Button variant="secondary" size="sm" icon="chevronLeft" onClick={() => setStep((n) => n - 1)}>Back</Button>}
            {last
              ? <Button variant="primary" size="sm" icon="check" onClick={onClose}>Done</Button>
              : <Button variant="primary" size="sm" iconRight="chevronRight" onClick={() => setStep((n) => n + 1)}>Next</Button>}
          </div>
        </div>
      </div>
    </>
  );
}

export default function UserGuide() {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  // The floating button only shows until the guide is first skipped/finished;
  // after that it's re-opened from the account menu (window event).
  const [seen, setSeen] = useState(true);

  useEffect(() => {
    let firstVisit = false;
    try { firstVisit = !localStorage.getItem(SEEN_KEY); } catch (_) { /* ignore */ }
    if (firstVisit) { setSeen(false); setOpen(true); }
    const reopen = () => setOpen(true);
    window.addEventListener("opus:open-guide", reopen);
    return () => window.removeEventListener("opus:open-guide", reopen);
  }, []);

  const close = () => {
    setOpen(false);
    setSeen(true);
    try { localStorage.setItem(SEEN_KEY, "1"); } catch (_) { /* ignore */ }
  };

  return (
    <>
      {!seen && (
        <button type="button" onClick={() => setOpen(true)}
          onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} aria-label="Open user guide"
          style={{
            position: "fixed", right: 22, bottom: 22, zIndex: 250, display: "flex", alignItems: "center", gap: 9,
            height: 46, padding: hover ? "0 18px 0 15px" : "0 13px", borderRadius: 999, cursor: "pointer",
            background: "var(--coral)", color: "#fff", border: "none", fontFamily: "var(--font-sans)",
            fontSize: 14, fontWeight: 600, boxShadow: "0 8px 24px rgba(255,69,92,0.35)", transition: "padding .16s",
          }}>
          <Icon name="info" size={20} />
          {hover && <span style={{ whiteSpace: "nowrap" }}>User Guide</span>}
        </button>
      )}
      <GuideTour open={open} onClose={close} />
    </>
  );
}
